// src/pages/Blogs/BlogForm.jsx
// Used for both Create (/blogs/create) and Edit (/blogs/edit/:id)
// Requires: npm install quill

import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getBlogById, createBlog, updateBlog } from "./BlogService";
import MainLayout from "../../layouts/MainLayout";
import "./Blogs.css";

/* ── Quill loaded directly (React 19 compatible) ── */
import Quill from "quill";
import "quill/dist/quill.snow.css";

const QUILL_OPTIONS = {
  theme: "snow",
  placeholder: "Write your blog content here…",
  modules: {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["blockquote", "code-block"],
      ["link", "image"],
      ["clean"],
    ],
  },
};

/* ── Constants ── */
const STATUS_OPTIONS   = ["Active", "Inactive", "Draft"];
const PRIORITY_OPTIONS = ["High", "Medium", "Low"];
const TYPE_OPTIONS     = ["Article", "Video", "Podcast", "Infographic"];
const CATEGORY_OPTIONS = ["Health", "Wellness", "Nutrition", "Mental Health", "Fitness", "Other"];

const INITIAL = {
  title: "", doctorName: "", companyName: "",
  category: "", type: "", status: "", priority: "", videoUrl: "",
};

/* ── Reusable UI primitives ── */

function FieldLabel({ children, required }) {
  return (
    <label style={{
      display: "block", fontSize: "0.75rem", fontWeight: 600,
      color: "#64748b", marginBottom: 6, letterSpacing: "0.01em",
    }}>
      {children}
      {required && <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span>}
    </label>
  );
}

function SectionHeading({ children }) {
  return (
    <div style={{
      fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.09em",
      textTransform: "uppercase", color: "#94a3b8",
      borderBottom: "1px solid #e2e8f0", paddingBottom: 8,
      marginBottom: 16, marginTop: 28,
    }}>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: "1px solid #e2e8f0", fontSize: "0.875rem",
  fontFamily: "'Inter', sans-serif", color: "#0f172a",
  outline: "none", background: "#fff", boxSizing: "border-box",
  transition: "border-color .15s",
};

function InputField({ label, required, ...props }) {
  return (
    <div>
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      <input
        style={inputStyle}
        onFocus={e => e.target.style.borderColor = "#0ea5e9"}
        onBlur={e  => e.target.style.borderColor = "#e2e8f0"}
        {...props}
      />
    </div>
  );
}

function SelectField({ label, options, value, onChange, name }) {
  return (
    <div>
      {label && <FieldLabel>{label}</FieldLabel>}
      <select
        name={name} value={value} onChange={onChange}
        style={{
          ...inputStyle, cursor: "pointer", appearance: "none",
          color: value ? "#0f172a" : "#94a3b8",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
          paddingRight: 32,
        }}
        onFocus={e => e.target.style.borderColor = "#0ea5e9"}
        onBlur={e  => e.target.style.borderColor = "#e2e8f0"}
      >
        <option value="">Select…</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function UploadBox({ label, preview, onChange }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <label
        style={{
          display: "block", border: "2px dashed #e2e8f0", borderRadius: 10,
          padding: preview ? 8 : "24px 16px", textAlign: "center",
          cursor: "pointer", background: "#f8fafc",
          transition: "border-color .18s, background .18s",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "#0ea5e9"; e.currentTarget.style.background = "#e0f2fe"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.background = "#f8fafc"; }}
      >
        <input type="file" accept="image/*" onChange={onChange} style={{ display: "none" }} />
        {preview ? (
          <img src={preview} alt={label}
            style={{ width: "100%", maxHeight: 150, objectFit: "cover", borderRadius: 8 }} />
        ) : (
          <>
            <div style={{ fontSize: "1.5rem", marginBottom: 6 }}>🖼️</div>
            <div style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 500 }}>
              <span style={{ color: "#0ea5e9", fontWeight: 600 }}>Click to upload</span>
            </div>
            <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginTop: 4 }}>PNG, JPG, WEBP</div>
          </>
        )}
      </label>
      {preview && (
        <p style={{ fontSize: "0.72rem", color: "#0ea5e9", marginTop: 4, textAlign: "center" }}>
          Click to change
        </p>
      )}
    </div>
  );
}

/* ── Quill Editor Component (React 19 safe) ── */
function QuillEditor({ value, onChange }) {
  const containerRef = useRef(null);
  const quillRef     = useRef(null);
  const onChangeRef  = useRef(onChange);

  // keep ref in sync without re-mounting
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    if (quillRef.current) return; // mount once only

    const container = containerRef.current;
    const editorDiv = document.createElement("div");
    container.appendChild(editorDiv);

    const quill = new Quill(editorDiv, QUILL_OPTIONS);
    quillRef.current = quill;

    // set initial content
    if (value) quill.clipboard.dangerouslyPasteHTML(value);

    quill.on(Quill.events.TEXT_CHANGE, () => {
      onChangeRef.current(quill.getSemanticHTML());
    });

    return () => {
      quill.off(Quill.events.TEXT_CHANGE);
      quillRef.current = null;
      container.innerHTML = "";
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sync external value → editor (edit mode load)
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill || !value) return;
    const current = quill.getSemanticHTML();
    if (current !== value) {
      quill.clipboard.dangerouslyPasteHTML(value);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value === "" || !quillRef.current]); // only on reset / first load

  return <div ref={containerRef} className="bform-quill-container" />;
}

/* ── Main Component ── */

export default function BlogForm() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isEdit   = Boolean(id);

  const [values, setValues]             = useState(INITIAL);
  const [content, setContent]           = useState("");
  const [thumbnail, setThumbnail]       = useState(null);
  const [image, setImage]               = useState(null);
  const [thumbPreview, setThumbPreview] = useState("");
  const [imgPreview, setImgPreview]     = useState("");
  const [loading, setLoading]           = useState(false);
  const [fetching, setFetching]         = useState(isEdit);
  const [toast, setToast]               = useState(null);
  const [initialContent, setInitialContent] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      const res = await getBlogById(id);
      const b   = res.blog;
      if (!b) { setFetching(false); return; }
      setValues({
        title:       b.title                        || "",
        doctorName:  b.doctorName                   || "",
        companyName: b.partnerName || b.companyName || "",
        category:    b.category                     || "",
        type:        b.type                         || "",
        status:      b.status                       || "",
        priority:    b.priority                     || "",
        videoUrl:    b.videoUrl                     || "",
      });
      const html = b.content || "";
      setContent(html);
      setInitialContent(html);
      if (b.thumbnail) setThumbPreview(b.thumbnail);
      if (b.image)     setImgPreview(b.image);
      setFetching(false);
    })();
  }, [id, isEdit]);

  const handleChange = e => setValues(v => ({ ...v, [e.target.name]: e.target.value }));

  const handleFile = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (type === "thumb") { setThumbnail(file); setThumbPreview(url); }
    else                  { setImage(file);     setImgPreview(url);   }
  };

  const handleSubmit = async () => {
    if (!values.title.trim()) {
      setToast({ type: "error", msg: "Title is required." });
      return;
    }
    setLoading(true);
    setToast(null);

    const fd = new FormData();
    fd.append("Title",       values.title);
    fd.append("DoctorName",  values.doctorName);
    fd.append("CompanyName", values.companyName);
    fd.append("Category",    values.category);
    fd.append("Type",        values.type);
    fd.append("Status",      values.status);
    fd.append("Piority",     values.priority);
    fd.append("VideoUrl",    values.videoUrl);
    fd.append("Content",     content);
    if (thumbnail) fd.append("thumbnailimg", thumbnail);
    if (image)     fd.append("imageFile",    image);
    if (isEdit)    fd.append("Id",           id);

    try {
      const res = isEdit ? await updateBlog(fd) : await createBlog(fd);
      if (res?.success || res?.id || res?.blogId) {
        setToast({ type: "success", msg: `Blog ${isEdit ? "updated" : "created"} successfully!` });
        setTimeout(() => navigate("/blogs"), 1200);
      } else {
        setToast({ type: "error", msg: res?.message || "Something went wrong." });
      }
    } catch {
      setToast({ type: "error", msg: "Network error. Please check your connection." });
    }
    setLoading(false);
  };

  if (fetching) {
    return (
      <MainLayout>
        <div className="blog-state-wrap">
          <span style={{ fontSize: "1.8rem" }}>⏳</span>
          <p>Loading blog data…</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="blog-form-page">

        {/* Header */}
        <div className="blog-form-header">
          <h2 className="blog-form-title">
            {isEdit ? <>Edit <span>Blog</span></> : <>Create <span>Blog</span></>}
          </h2>
          <Link to="/blogs" className="btn-cancel-blog">← Back to Blogs</Link>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`blog-form-toast ${toast.type}`}>
            {toast.type === "success" ? "✅" : "❌"} {toast.msg}
          </div>
        )}

        {/* Form Card */}
        <div className="blog-form-card">

          {/* BASIC INFO */}
          <SectionHeading>Basic Information</SectionHeading>
          <div className="bform-grid-full">
            <InputField label="Title" name="title" required
              value={values.title} onChange={handleChange}
              placeholder="Enter blog title…"
            />
          </div>
          <div className="bform-grid-2" style={{ marginTop: 12 }}>
            <InputField label="Doctor / Author Name" name="doctorName"
              value={values.doctorName} onChange={handleChange}
              placeholder="Dr. John Doe"
            />
            <InputField label="Company / Partner Name" name="companyName"
              value={values.companyName} onChange={handleChange}
              placeholder="e.g. MedCorp"
            />
          </div>

          {/* CLASSIFICATION */}
          <SectionHeading>Classification</SectionHeading>
          <div className="bform-grid-4">
            <SelectField label="Category" name="category" value={values.category} onChange={handleChange} options={CATEGORY_OPTIONS} />
            <SelectField label="Type"     name="type"     value={values.type}     onChange={handleChange} options={TYPE_OPTIONS}     />
            <SelectField label="Status"   name="status"   value={values.status}   onChange={handleChange} options={STATUS_OPTIONS}   />
            <SelectField label="Priority" name="priority" value={values.priority} onChange={handleChange} options={PRIORITY_OPTIONS} />
          </div>

          {/* MEDIA */}
          <SectionHeading>Media</SectionHeading>
          <div className="bform-grid-3">
            <UploadBox label="Thumbnail Image" preview={thumbPreview} onChange={e => handleFile(e, "thumb")} />
            <UploadBox label="Featured Image"  preview={imgPreview}   onChange={e => handleFile(e, "image")} />
            <InputField label="Video URL (optional)" name="videoUrl"
              value={values.videoUrl} onChange={handleChange}
              placeholder="https://youtube.com/watch?v=…"
            />
          </div>

          {/* CONTENT */}
          <SectionHeading>Content</SectionHeading>
          <div className="bform-quill-wrap">
            <QuillEditor value={initialContent} onChange={setContent} />
          </div>

          {/* ACTIONS */}
          <div className="blog-form-actions">
            <button className="btn-submit-blog" onClick={handleSubmit} disabled={loading}>
              {loading
                ? (isEdit ? "Saving…" : "Publishing…")
                : (isEdit ? "💾 Save Changes" : "🚀 Publish Blog")}
            </button>
            <Link to="/blogs" className="btn-cancel-blog">Cancel</Link>
          </div>

        </div>
      </div>
    </MainLayout>
  );
}